import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CharacterData, ComputedStats } from '../../types/character';
import { CharacterStatComputer } from '../../utils/CharacterStatComputer';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import Input from '../Common/Input';
import StatTooltip from '../Common/StatTooltip';
import Button from '../Common/Button';
import './Traits.css';

interface TraitsProps {
  enhancedCharacter: EnhancedCharacter;
  updateCharacterData: (updates: any) => void;
}

const Traits: React.FC<TraitsProps> = ({ enhancedCharacter, updateCharacterData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  if (!enhancedCharacter.character) {
    return (
      <div className="traits">
        <div className="traits__loading">
          Loading traits...
        </div>
      </div>
    );
  }

  const character = enhancedCharacter.character;

  const handleEdit = () => {
    // Initialize edit values with current base stats
    const initialValues: Record<string, string> = {};
    traits.forEach(({ key }) => {
      initialValues[key] = character.baseStats.traits[key].toString();
    });
    setEditValues(initialValues);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Convert edit values back to numbers and update
    const updatedTraits: Record<string, number> = {};
    Object.entries(editValues).forEach(([key, value]) => {
      updatedTraits[key] = parseInt(value, 10) || 0;
    });

    updateCharacterData({
      traits: {
        ...character.baseStats.traits,
        ...updatedTraits,
      },
    });
    
    setIsEditing(false);
    setEditValues({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues({});
  };

  const handleEditValueChange = (trait: string) => (value: string) => {
    setEditValues(prev => ({
      ...prev,
      [trait]: value,
    }));
  };

  const traits = [
    { key: 'agility', label: 'Agility' },
    { key: 'strength', label: 'Strength' },
    { key: 'finesse', label: 'Finesse' },
    { key: 'instinct', label: 'Instinct' },
    { key: 'presence', label: 'Presence' },
    { key: 'knowledge', label: 'Knowledge' },
  ] as const;

  return (
    <div className="traits">
      {/* Traits grid */}
      <div className="traits__grid">
        {traits.map(({ key, label }) => {
          const baseValue = character.baseStats.traits[key];
          const computedValue = enhancedCharacter.computedStats?.traits[key] ?? baseValue;
          const hasModification = computedValue !== baseValue;
          const breakdown = enhancedCharacter.statComputer?.getModifierBreakdown(`traits.${key}`);
          
          // Display value: show computed value in display mode, base value in edit mode
          const displayValue = isEditing 
            ? (editValues[key] ?? baseValue.toString())
            : computedValue.toString();

          return (
            <motion.div 
              key={key} 
              className="traits__trait"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: traits.findIndex(t => t.key === key) * 0.1 }}
            >
              <div className="traits__input-container">
                <StatTooltip
                  breakdown={!isEditing && hasModification ? breakdown : undefined}
                  statName={label}
                >
                  <Input
                    value={displayValue}
                    onChange={isEditing ? handleEditValueChange(key) : undefined}
                    label={label}
                    placeholder="0"
                    className={`traits__input ${
                      !isEditing && hasModification ? 'traits__input--modified' : ''
                    }`}
                    readOnly={!isEditing}
                  />
                </StatTooltip>
              </div>

            </motion.div>
          );
        })}
      </div>
      
      {/* Edit/Save/Cancel buttons */}
      <div className="traits__actions">
        {!isEditing ? (
          <Button
            variant="SECONDARY"
            size="SMALL"
            onClick={handleEdit}
            className="traits__edit-button"
          >
            Edit
          </Button>
        ) : (
          <div className="traits__edit-actions">
            <Button
              variant="PRIMARY"
              size="SMALL"
              onClick={handleSave}
              className="traits__save-button"
            >
              Save
            </Button>
            <Button
              variant="CLEAR"
              size="SMALL"
              onClick={handleCancel}
              className="traits__cancel-button"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Traits;